/*
 * Reading Robinhood Chain from a player's page while it pays a USDG entry (a wallet's USDG and ETH, what it lets the
 * vault take, whether a transaction went through). The chain's public address is asked directly; when the page can't
 * reach it (a browser or a network that refuses it), the game server reads the same things instead
 * (/api/entry/chain/…), and does for the rest of the page's life. No wallet app is involved in the reads.
 */
import { encodeFunctionData, parseAbi } from "viem/utils";

export type TransactionState = "pending" | "success" | "failed";

export interface ChainReads {
	usdgBalance(address: `0x${string}`): Promise<bigint>;
	ethBalance(address: `0x${string}`): Promise<bigint>;
	/** a wallet's USDG and ETH together (one question to the game server, when it reads for the page) */
	walletBalances(address: `0x${string}`): Promise<{ readonly usdg: bigint; readonly eth: bigint }>;
	/** what a wallet lets the USDG rooms' vault take of its USDG (its approval) */
	usdgAllowance(owner: `0x${string}`): Promise<bigint>;
	/** pending (not in a block yet), success, or failed */
	transaction(hash: `0x${string}`): Promise<TransactionState>;
	/** a call on the public address only (a fee estimate): undefined when the page can't reach it */
	direct<T>(method: string, params: readonly unknown[]): Promise<T | undefined>;
}

const ERC20 = parseAbi(["function balanceOf(address owner) view returns (uint256)", "function allowance(address owner, address spender) view returns (uint256)"]);

/** the page couldn't reach the chain's public address (no answer, or not a JSON-RPC one) */
class Unreachable extends Error {}

interface RpcAnswer<T> {
	readonly result?: T;
	readonly error?: { readonly message?: string };
}

export function chainReads(setup: {
	/** the chain's public address, when the game server names one */
	readonly rpcUrl?: string;
	readonly usdgToken: `0x${string}`;
	/** the USDG rooms' vault (what an approval lets take) */
	readonly usdgVault?: `0x${string}`;
	/** the game server's API (API_URL) */
	readonly apiBase: string;
}): ChainReads {
	let id = 0;
	let directWorks = setup.rpcUrl !== undefined;
	const rpc = async <T>(method: string, params: readonly unknown[]): Promise<T> => {
		if (!setup.rpcUrl) throw new Unreachable("no public address");
		let answer: RpcAnswer<T>;
		try {
			const response = await fetch(setup.rpcUrl, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ jsonrpc: "2.0", id: ++id, method, params }),
			});
			answer = (await response.json()) as RpcAnswer<T>;
		} catch (error) {
			throw new Unreachable(error instanceof Error ? error.message : String(error));
		}
		if (answer.error) throw new Error(answer.error.message ?? `${method} failed`);
		if (!("result" in answer)) throw new Unreachable(`${method}: no result`);
		return answer.result as T;
	};
	const fromServer = async <T>(path: string): Promise<T> => {
		const response = await fetch(`${setup.apiBase}${path}`, { credentials: "include" });
		const body = (await response.json().catch(() => ({}))) as T & { readonly error?: string };
		if (!response.ok) throw new Error(body.error ?? `The game server couldn't read the blockchain (${response.status})`);
		return body;
	};
	/** asked of the public address while it answers; of the game server once it hasn't */
	const either = async <T>(direct: () => Promise<T>, viaServer: () => Promise<T>): Promise<T> => {
		if (directWorks) {
			try {
				return await direct();
			} catch (error) {
				if (!(error instanceof Unreachable)) throw error;
				directWorks = false;
				console.warn("The chain's public address didn't answer this page: the game server reads it instead", error.message);
			}
		}
		return viaServer();
	};
	const balances = (address: `0x${string}`) =>
		fromServer<{ readonly usdg: string; readonly eth: string; readonly allowance?: string }>(`/entry/chain/balances?address=${encodeURIComponent(address)}`);
	const usdgDirect = async (address: `0x${string}`): Promise<bigint> =>
		BigInt(
			await rpc<string>("eth_call", [
				{ to: setup.usdgToken, data: encodeFunctionData({ abi: ERC20, functionName: "balanceOf", args: [address] }) },
				"latest",
			]),
		);
	const ethDirect = async (address: `0x${string}`): Promise<bigint> => BigInt(await rpc<string>("eth_getBalance", [address, "latest"]));
	return {
		usdgBalance: (address) =>
			either(
				() => usdgDirect(address),
				async () => BigInt((await balances(address)).usdg),
			),
		ethBalance: (address) =>
			either(
				() => ethDirect(address),
				async () => BigInt((await balances(address)).eth),
			),
		walletBalances: (address) =>
			either(
				async () => {
					const [usdg, eth] = await Promise.all([usdgDirect(address), ethDirect(address)]);
					return { usdg, eth };
				},
				async () => {
					const read = await balances(address);
					return { usdg: BigInt(read.usdg), eth: BigInt(read.eth) };
				},
			),
		usdgAllowance: (owner) => {
			const vault = setup.usdgVault;
			if (!vault) return Promise.reject(new Error("No vault to ask about"));
			return either(
				async () =>
					BigInt(
						await rpc<string>("eth_call", [
							{ to: setup.usdgToken, data: encodeFunctionData({ abi: ERC20, functionName: "allowance", args: [owner, vault] }) },
							"latest",
						]),
					),
				async () => {
					const read = (await balances(owner)).allowance;
					if (read === undefined) throw new Error("The game server doesn't read approvals");
					return BigInt(read);
				},
			);
		},
		transaction: (hash) =>
			either(
				async (): Promise<TransactionState> => {
					const receipt = await rpc<{ readonly status?: string } | null>("eth_getTransactionReceipt", [hash]);
					return receipt ? (receipt.status === "0x1" ? "success" : "failed") : "pending";
				},
				async () => (await fromServer<{ readonly state: TransactionState }>(`/entry/chain/transactions/${hash}`)).state,
			),
		async direct<T>(method: string, params: readonly unknown[]): Promise<T | undefined> {
			if (!directWorks) return undefined;
			try {
				return await rpc<T>(method, params);
			} catch (error) {
				if (!(error instanceof Unreachable)) throw error;
				directWorks = false;
				return undefined;
			}
		},
	};
}
